import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Page, Left, Right, HeaderRow, RoleListContainer, RoleItem, SearchInput } from "../styles/component.style";
import { BtnGeneral } from "../../../../styles/Buttons";
import SearchableSelect from "../../../../components/Common/SearchableSelect";
import { useEmpresa } from "../../../../hooks/useEmpresa";
import { FaBuilding, FaCamera, FaTrash } from "react-icons/fa";

export default function Empresa() {
    const {
        empresas,
        search,
        setSearch,
        selected,
        form,
        setForm,
        handleSelect,
        handleNew,
        saveEmpresa,
        handleLogoChange,
        handleRemoveLogo,
    } = useEmpresa();

    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const filtered = empresas.filter((e) =>
        (e.nombre || "").toLowerCase().includes(search.toLowerCase()) ||
        String(e.numeroIdentificacion || "").includes(search)
    );

    const onFileInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleLogoChange(file);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleLogoChange(file);
        }
    };

    return (
        <Page>
            {/* IZQUIERDA: LISTA DE EMPRESAS */}
            <Left>
                <HeaderRow>
                    <ListTitle>Empresas</ListTitle>
                    <BtnGeneral bg="cyan" padding="6px 12px" radius="6px" fw="600" onClick={handleNew}>
                        Nuevo
                    </BtnGeneral>
                </HeaderRow>
                <SearchInput
                    placeholder="Buscar..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <RoleListContainer>
                    {filtered.map((e) => (
                        <StyledRoleItem
                            key={e.id}
                            active={selected === e.id}
                            onClick={() => handleSelect(e)}
                        >
                            <CompanyThumb>
                                {e.logo ? (
                                    <img src={e.logo} alt="" />
                                ) : (
                                    <FaBuilding size={13} color="#94a3b8" />
                                )}
                            </CompanyThumb>
                            <CompanyInfo>
                                <strong>{e.nombre}</strong>
                                <small>{e.tipoIdentificacion} {e.numeroIdentificacion}</small>
                            </CompanyInfo>
                        </StyledRoleItem>
                    ))}
                </RoleListContainer>
            </Left>

            {/* DERECHA: FORMULARIO */}
            <Right>
                <HeaderRow style={{ marginBottom: "20px" }}>
                    <FormTitle>{form.id ? "Editar Empresa" : "Nueva Empresa"}</FormTitle>
                    <BtnGeneral bg="green" padding="6px 16px" radius="6px" fw="600" onClick={saveEmpresa}>
                        Guardar
                    </BtnGeneral>
                </HeaderRow>

                <FormContainer>
                    {/* COLUMNA 1: IDENTIFICACIÓN */}
                    <FormColumn>
                        <FormGroup>
                            <Label>Código Prestador *</Label>
                            <StyledInput
                                placeholder="Ej: 13001001"
                                value={form.codigo}
                                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Nombre de la Empresa *</Label>
                            <StyledInput
                                placeholder="Ej: HOSPITAL LOCAL E.S.E."
                                value={form.nombre}
                                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Tipo Identificación *</Label>
                            <SearchableSelect
                                value={form.tipoIdentificacion || "NI"}
                                onValueChange={(val) => setForm({ ...form, tipoIdentificacion: val })}
                                options={[
                                    { value: "NI", label: "NI (Nit)" },
                                    { value: "CC", label: "CC" },
                                ]}
                                multiple={false}
                                searchable={false}
                                size="small"
                                sx={selectSx}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Número Identificación *</Label>
                            <StyledInput
                                placeholder="Ej: 890123456"
                                value={form.numeroIdentificacion}
                                onChange={(e) => setForm({ ...form, numeroIdentificacion: e.target.value })}
                            />
                        </FormGroup>
                    </FormColumn>

                    {/* COLUMNA 2: CONTACTO Y ESTADO */}
                    <FormColumn>
                        <FormGroup>
                            <Label>Dirección</Label>
                            <StyledInput
                                placeholder="Ej: Calle 10 # 5-20"
                                value={form.direccion}
                                onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Correo Electrónico</Label>
                            <StyledInput
                                type="email"
                                placeholder="correo@empresa.com"
                                value={form.correo}
                                onChange={(e) => setForm({ ...form, correo: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Teléfono</Label>
                            <StyledInput
                                placeholder="Ej: 3001234567"
                                value={form.telefono}
                                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Estado</Label>
                            <SearchableSelect
                                value={String(form.estado ?? 1)}
                                onValueChange={(val) => setForm({ ...form, estado: Number(val) })}
                                options={[
                                    { value: "1", label: "ACTIVO" },
                                    { value: "0", label: "INACTIVO" },
                                ]}
                                multiple={false}
                                searchable={false}
                                size="small"
                                sx={selectSx}
                            />
                        </FormGroup>
                    </FormColumn>

                    {/* COLUMNA 3: LOGO COMPACTO Y ACOMODADO A UN LADO */}
                    <LogoColumn>
                        <Label style={{ textAlign: "center", width: "100%" }}>Logo</Label>

                        <LogoBox
                            $isDragging={isDragging}
                            $hasLogo={Boolean(form.logo)}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            title={form.logo ? "Haz clic para cambiar el logo" : "Haz clic o arrastra para subir el logo"}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml"
                                onChange={onFileInputChange}
                                style={{ display: "none" }}
                            />

                            {form.logo ? (
                                <>
                                    <LogoImg src={form.logo} alt="Logo empresa" />
                                    <LogoHoverOverlay className="hover-overlay">
                                        <FaCamera size={15} />
                                        <span>Cambiar</span>
                                    </LogoHoverOverlay>
                                </>
                            ) : (
                                <LogoEmptyState>
                                    <FaCamera size={22} color="#94a3b8" />
                                    <span>Cargar logo</span>
                                    <small>PNG, JPG, SVG</small>
                                </LogoEmptyState>
                            )}
                        </LogoBox>

                        {form.logo && (
                            <RemoveLogoBtn
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveLogo();
                                }}
                                title="Quitar logo"
                            >
                                <FaTrash size={10} />
                                <span>Quitar</span>
                            </RemoveLogoBtn>
                        )}
                    </LogoColumn>
                </FormContainer>
            </Right>
        </Page>
    );
}

// ==========================================
// STYLED COMPONENTS (Protocolo APIM)
// ==========================================

const ListTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-title, #0f172a);
`;

const FormTitle = styled.h3`
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 600;
  margin: 0;
  color: var(--color-text-title, #0f172a);
`;

const StyledRoleItem = styled(RoleItem)`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
`;

const CompanyThumb = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #ffffff;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  strong {
    font-size: 13px;
    font-family: var(--font-body);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  small {
    font-size: 11px;
    color: #64748b;
    font-family: var(--font-body);
  }
`;

const FormContainer = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const FormColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const Label = styled.label`
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 4px;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  box-sizing: border-box;
  font-family: var(--font-body);
  font-size: 13px;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #007bba;
    box-shadow: 0 0 0 2px rgba(0, 123, 186, 0.15);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

// COLUMNA LATERAL DEL LOGO (COMPACTO)
const LogoColumn = styled.div`
  width: 120px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LogoBox = styled.div`
  width: 110px;
  height: 110px;
  border-radius: 8px;
  border: 1px dashed ${(props) => (props.$isDragging ? "#0284c7" : props.$hasLogo ? "#cbd5e1" : "#94a3b8")};
  background-color: ${(props) =>
    props.$isDragging ? "#f0f9ff" : props.$hasLogo ? "#ffffff" : "#f8fafc"};
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  overflow: hidden;
  box-sizing: border-box;
  padding: 6px;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: #0284c7;
    background-color: ${(props) => (props.$hasLogo ? "#ffffff" : "#f0f9ff")};

    .hover-overlay {
      opacity: 1;
    }
  }
`;

const LogoImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const LogoHoverOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #ffffff;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.2s ease;
`;

const LogoEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  user-select: none;

  span {
    font-family: var(--font-body);
    font-size: 11px;
    font-weight: 600;
    color: #475569;
  }

  small {
    font-family: var(--font-body);
    font-size: 9px;
    color: #94a3b8;
  }
`;

const RemoveLogoBtn = styled.button`
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  background: transparent;
  border: 1px solid #fecaca;
  color: #dc2626;
  border-radius: 5px;
  padding: 3px 8px;
  font-family: var(--font-body);
  font-size: 11px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #fef2f2;
    border-color: #dc2626;
  }
`;

const selectSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "var(--font-body)",
    backgroundColor: "#ffffff",
    "& fieldset": { borderColor: "#cbd5e1" },
    "&:hover fieldset": { borderColor: "#94a3b8" },
    "&.Mui-focused fieldset": { borderColor: "#007bba" },
  },
  "& .MuiSelect-select": {
    padding: "8px 12px !important",
    fontSize: "13px",
    fontFamily: "var(--font-body)",
  },
};
